<template>
  <article class="event-page conference-page">
    <EventHero :event="event" />
    <EventSectionNav :items="navigation" />
    <section v-if="event.summary || event.conference" id="conference-summary" class="conference-overview">
      <v-container>
        <p>Conference Summary</p>
        <div class="overview-grid">
          <div>
            <h2>A Conference Built Into the City</h2>
            <div v-if="event.summaryParagraphs && event.summaryParagraphs.length" class="summary-copy">
              <p v-for="paragraph in event.summaryParagraphs" :key="paragraph">{{ paragraph }}</p>
            </div>
            <p v-else-if="event.summary" class="summary-copy">{{ event.summary }}</p>
            <div v-if="event.conference && event.conference.purpose" class="purpose-card">
              <strong>Why we went</strong>
              <span>{{ event.conference.purpose }}</span>
            </div>
          </div>

          <aside v-if="event.conference" class="conference-details">
            <h3>{{ event.conference.conferenceName }}</h3>
            <p v-if="event.conference.attendanceReason" class="conference-copy">
              {{ event.conference.attendanceReason }}
            </p>
            <dl>
              <div v-if="event.conference.conferenceDates">
                <dt>Conference dates</dt><dd>{{ event.conference.conferenceDates }}</dd>
              </div>
              <div v-if="event.conference.venueNames && event.conference.venueNames.length">
                <dt>Conference footprint</dt><dd>{{ event.conference.venueNames.join(' · ') }}</dd>
              </div>
              <div v-if="event.conference.memorableSessions && event.conference.memorableSessions.length">
                <dt>Sessions that stayed with us</dt><dd>{{ event.conference.memorableSessions.join(' · ') }}</dd>
              </div>
            </dl>
            <a
              v-if="event.conference.conferenceUrl"
              :href="event.conference.conferenceUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              Visit conference site <v-icon small>mdi-open-in-new</v-icon>
            </a>
          </aside>
        </div>
      </v-container>
    </section>
    <EventStats
      :items="event.atAGlance"
      title="At a Glance"
      kicker="The Essentials"
      section-id="at-a-glance"
    />
    <EventStats
      :items="event.tripByNumbers"
      title="Trip by the Numbers"
      section-id="trip-by-numbers"
    />
    <ExperienceDNA :items="event.experienceDNA" include-conference />
    <TripStoryTimeline :event="event" :entity-by-id="entityById" />

    <EntitySection
      :items="event.sessions"
      :entity-by-id="entityById"
      section-id="sessions"
      title="Sessions Worth Remembering"
      kicker="Ideas and Inspiration"
    />
    <EntitySection
      :items="event.venues"
      :entity-by-id="entityById"
      section-id="venues"
      :title="sectionCopy.venues.title"
      kicker="Featured Venues"
      :description="sectionCopy.venues.description"
    />
    <EntitySection
      :items="event.restaurants"
      :entity-by-id="entityById"
      section-id="dining"
      title="Eats Along the Way"
      kicker="Food, Pubs, and Breweries"
      :description="sectionCopy.dining.description"
      :event="event"
      detail-enabled
    />
    <EntitySection
      :items="event.activities"
      :entity-by-id="entityById"
      section-id="activities"
      title="Activities and Team Experiences"
      kicker="The City Between Sessions"
      :event="event"
      detail-enabled
    />
    <EntitySection
      :items="event.hotels"
      :entity-by-id="entityById"
      section-id="hotel"
      title="Where We Stayed"
      kicker="Home Base"
    />
    <CrewSection :crew="event.crew" />
    <MeritBadgeSection :badges="event.meritBadges" :event="event" />
    <EventGallery :media="event.gallery" :title="`${event.title} Moments`" />
    <section v-if="event.closingReflection" id="reflection" class="closing-reflection">
      <v-container>
        <p>Ideas We Brought Home</p>
        <blockquote>{{ event.closingReflection }}</blockquote>
      </v-container>
    </section>
  </article>
</template>

<script>
import EventHero from '../event/EventHero.vue';
import EventSectionNav from '../event/EventSectionNav.vue';
import EventStats from '../stats/EventStats.vue';
import ExperienceDNA from '../stats/ExperienceDNA.vue';
import TripStoryTimeline from '../story/TripStoryTimeline.vue';
import EntitySection from '../entities/EntitySection.vue';
import CrewSection from '../people/CrewSection.vue';
import MeritBadgeSection from '../badges/MeritBadgeSection.vue';
import EventGallery from '../media/EventGallery.vue';

export default {
  name: 'ConferenceEventPage',
  components: {
    EventHero,
    EventSectionNav,
    EventStats,
    ExperienceDNA,
    TripStoryTimeline,
    EntitySection,
    CrewSection,
    MeritBadgeSection,
    EventGallery,
  },
  props: {
    event: { type: Object, required: true },
    entityById: { type: Object, required: true },
  },
  computed: {
    sectionCopy() {
      const content = this.event.sectionContent || {};
      return {
        venues: {
          title: 'The Conference Across the City',
          description: 'The conference venues turned the surrounding city into an extension of the program.',
          ...(content.venues || {}),
        },
        dining: {
          description: 'Coffee, meals, and local favorites kept the story moving between conference sessions.',
          ...(content.dining || {}),
        },
      };
    },
    navigation() {
      return [
        { id: 'conference-summary', label: 'Overview', show: true },
        { id: 'story-timeline', label: 'Story Timeline', show: true },
        { id: 'sessions', label: 'Sessions', show: this.event.sessions?.length },
        { id: 'venues', label: 'Venues', show: this.event.venues?.length },
        { id: 'dining', label: 'Eats', show: this.event.restaurants?.length },
        { id: 'activities', label: 'Activities', show: this.event.activities?.length },
        { id: 'hotel', label: 'Hotel', show: this.event.hotels?.length },
        { id: 'crew', label: 'Crew', show: this.event.crew?.length },
        { id: 'badges', label: 'Badges', show: this.event.meritBadges?.length },
        { id: 'gallery', label: 'Gallery', show: this.event.gallery?.length },
      ].filter((item) => item.show);
    },
  },
};
</script>

<style scoped>
.event-page { background: #050505; }
.conference-overview { background: linear-gradient(135deg, #16120c, #080808 70%); border-bottom: 1px solid #2d2a20; border-top: 1px solid #2d2a20; padding: 72px 0; scroll-margin-top: 68px; }
.conference-overview > .container > p, .closing-reflection p { color: #e23232; font-family: "Oswald", sans-serif; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; }
.overview-grid { display: grid; gap: 42px; grid-template-columns: 1.1fr 0.9fr; }
.overview-grid h2 { color: #fff; font-family: "Oswald", sans-serif; font-size: clamp(2.4rem, 6vw, 4rem); line-height: 1; margin: 0 0 20px; }
.summary-copy { color: #d4d4d4; font-size: clamp(1.12rem, 2vw, 1.35rem); line-height: 1.7; margin: 0; }
.summary-copy p { margin: 0; }
.summary-copy p + p { margin-top: 16px; }
.purpose-card { border-left: 3px solid #f0b13c; color: #c9c9c9; display: grid; gap: 7px; margin-top: 28px; padding: 4px 0 4px 20px; }
.purpose-card strong { color: #fff; font-family: "Oswald", sans-serif; text-transform: uppercase; }
.purpose-card span { line-height: 1.65; }
.conference-details { background: rgba(255, 255, 255, 0.035); border: 1px solid #363024; border-radius: 12px; padding: 28px; }
.conference-details h3 { color: #fff; font-family: "Oswald", sans-serif; font-size: 1.8rem; line-height: 1.15; margin: 0 0 12px; }
.conference-copy { color: #d0d0d0; line-height: 1.65; margin: 0 0 24px; }
.overview-grid a { color: #f0b13c; font-weight: 800; text-decoration: none; }
.overview-grid a .v-icon { color: inherit; }
.overview-grid dl { margin: 0 0 24px; }
.overview-grid dl div + div { border-top: 1px solid #302c24; margin-top: 20px; padding-top: 20px; }
.overview-grid dt { color: #f0b13c; font-size: 0.78rem; font-weight: 800; text-transform: uppercase; }
.overview-grid dd { color: #d3d3d3; line-height: 1.55; margin: 6px 0 0; }
.closing-reflection { background: linear-gradient(140deg, #191006, #080808 75%); border-top: 1px solid #3a2b18; padding: 86px 0; scroll-margin-top: 68px; }
.closing-reflection blockquote { color: #fff; font-family: "Oswald", sans-serif; font-size: clamp(1.6rem, 4vw, 2.7rem); line-height: 1.35; margin: 0; max-width: 980px; }
@media (max-width: 760px) { .overview-grid { grid-template-columns: 1fr; } }
</style>
